/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
// import ZingTouch from 'zingtouch';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BottomSheetEnabled } from '../../../types/BottomSheetEnabled';
import { ReportService } from '../../reports/services/report.service';
import { LoggerService } from '../../shared/services/logger.service';
import { MapLegendComponent } from '../legend/map-legend.component';
import { ReloadMap } from '../state/active-map.actions';
import { MapToolbarComponent } from '../toolbar/map-toolbar.component';
import {
    FlexFillDirective,
    LayoutDirective,
    LayoutAlignDirective,
    FlexDirective,
} from '@ngbracket/ngx-layout/flex';
import { HotKeysDirective } from '../../../core/ui/hotkeys/hot-keys.directive';
import { MapComponent } from './map.component';
import { MapTipDirective } from './map-tip.directive';
import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import { MatFabButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass, AsyncPipe } from '@angular/common';
import { ClassDirective } from '@ngbracket/ngx-layout';
import { MatToolbar } from '@angular/material/toolbar';
import { MapContextMenuComponent } from '../context-menu/map-context-menu.component';

@Component({
    selector: 'dash-gis-map-container',
    templateUrl: './map-container.component.html',
    styleUrls: ['./map-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FlexFillDirective,
        HotKeysDirective,
        LayoutDirective,
        LayoutAlignDirective,
        MatSidenavContainer,
        MatSidenavContent,
        MapToolbarComponent,
        FlexDirective,
        MapComponent,
        MapTipDirective,
        CdkContextMenuTrigger,
        MatFabButton,
        MatTooltip,
        MatIcon,
        MatSidenav,
        MapLegendComponent,
        NgClass,
        ClassDirective,
        MatToolbar,
        MatIconButton,
        MapContextMenuComponent,
        AsyncPipe,
    ],
})
export class MapContainerComponent implements OnInit, OnDestroy {
    public initializedSubject = new BehaviorSubject<boolean>(false);
    public isFullWidth = true;
    @ViewChild('bottomSheetView', { read: ViewContainerRef })
    private bottomSheetContainerRef: ViewContainerRef;

    @ViewChild('sidenav', { read: MatSidenav })
    private sidenavRef: MatSidenav;

    private sidenavOpenSubject = new BehaviorSubject<boolean>(false);
    private disposeSubject = new Subject<void>();
    private dispose$: Observable<void>;

    constructor(
        private logger: LoggerService,
        private viewContainerRef: ViewContainerRef,
        private bottomSheet: MatBottomSheet,
        private elementRef: ElementRef,
        private media: MediaObserver,
        private store: Store,
        private reportService: ReportService,
        private actions$: Actions,
        private cdr: ChangeDetectorRef
    ) {
        this.dispose$ = this.disposeSubject.asObservable();

        this.media
            .asObservable()
            .pipe(
                map((changes) => changes.findIndex((c) => c.matches && c.mqAlias === 'gt-md') !== -1),
                takeUntil(this.dispose$)
            )
            .subscribe((result) => {
                this.isFullWidth = result;
            });
    }

    ngOnInit() {
        this.initialize();
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    // allow re-initializing without reloading
    initialize() {
        this.initializedSubject.next(false);
        this.cdr.detectChanges();

        this.actions$.pipe(ofActionSuccessful(ReloadMap), takeUntil(this.dispose$)).subscribe(() => this.reset());

        this.initializedSubject.next(true);
    }

    reset() {
        this.disposeSubject.next();
        this.initialize();
    }

    // enable the legend DOM before opening and after closing the sidenav/bottomsheet
    openSidenav() {
        this.sidenavOpenSubject.next(true);
        this.sidenavRef.open().then(() => {});
    }

    sidenavClosing() {
        this.sidenavOpenSubject.next(false);
    }

    openActionMenu() {
        const sheetRef = this.bottomSheet.open(MapToolbarComponent, {
            hasBackdrop: true,
            viewContainerRef: this.bottomSheetContainerRef,
            data: { isBottomSheet: true },
        });

        this.openSheet(sheetRef);
    }

    openLegendMenu() {
        const sheetRef = this.bottomSheet.open(MapLegendComponent, {
            hasBackdrop: true,
            viewContainerRef: this.bottomSheetContainerRef,
            data: {
                isBottomSheet: true,
                legendVisible: true,
            },
        });

        this.openSheet(sheetRef);
    }

    private openSheet<T extends BottomSheetEnabled>(sheetRef: MatBottomSheetRef<T>) {
        // const swipeHandler = (e: any) => {
        //     if (e.length === 1) {
        //         const event = e[0];
        //         if (event.direction && event.direction >= 250 && event.direction <= 290) {
        //             this.bottomSheet.dismiss();
        //         }
        //     }
        // };
        //
        // const ztRegion = new ZingTouch.Region(document.body);
        // const el = sheetRef.instance.elementRef.nativeElement;
        // sheetRef.afterOpened().subscribe(() => {
        //     ztRegion.bindOnce(el, 'swipe', swipeHandler, false);
        // });
        // sheetRef.afterDismissed().subscribe(() => {
        //     ztRegion.unbind(el);
        // });
    }

    get initialized$() {
        return this.initializedSubject.asObservable();
    }

    get sidenavOpen$() {
        return this.sidenavOpenSubject.asObservable();
    }
}
